import { Field, Form, Formik } from "formik";
import * as React from "react";
import * as Yup from "yup";
import { t } from "../../../i18n/util";
import { PutApplicantNotesPayload } from "../../../network/APITypes";
import { useMobileMediaQuery } from "../../hooks/useMobileMediaQuery";
import { useUsers } from "../../hooks/useUsers";
import { CustomAutoComplete } from "../../ui/CustomAutoComplete";
import { CustomInputField } from "../../ui/CustomInputField";
import { DialogHeader, ErrorMessagesList, FormButtons, HrBotDialog } from "../../ui/Primitives";

type Props = {
    initialValues: PutApplicantNotesPayload;
    onSubmit: (model: PutApplicantNotesPayload) => void;
    onBack?: () => void;
    isOpen: boolean;
    edit?: boolean;
    comment?: string;
};

export const NoteDialog = ({ initialValues, onSubmit, onBack, isOpen, edit, comment }: Props) => {
    const [open, setOpen] = React.useState(isOpen);
    const isMobile = useMobileMediaQuery();
    const users = useUsers()?.map((user) => user.username);

    React.useEffect(() => {
        setOpen(isOpen);
    }, [isOpen]);

    const handleClose = () => {
        setOpen(false);
        onBack?.();
    };

    const handleSubmit = (model: PutApplicantNotesPayload) => {
        setOpen(false);
        onSubmit(model);
    };

    const label = () => {
        if (edit) {
            return t("screen.applicantDetails.edit");
        } else if (comment) {
            return t("screen.applicantNotes.writeComment");
        } else {
            return t("screen.applicantNotes.create");
        }
    };

    return (
        <HrBotDialog open={open} maxWidth={isMobile ? undefined : false}>
            <div style={{ width: isMobile ? "unset" : 700 }}>
                <DialogHeader label={label()} onClose={handleClose} />
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={Yup.object().shape({
                        title: comment ? Yup.string() : Yup.string().required(t("error.required")),
                        content: Yup.string().required(t("error.required"))
                    })}
                    validateOnChange
                    validateOnBlur
                >
                    {({ errors, touched, setFieldValue, values }) => {
                        return (
                            <Form>
                                <div style={{ padding: isMobile ? "0 24px 24px" : "10px 24px 24px" }}>
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            height: "100%",
                                            gap: 16
                                        }}
                                    >
                                        {!comment && (
                                            <Field
                                                component={CustomInputField}
                                                label={t("screen.dashboard.dialog.title")}
                                                name="title"
                                                type="text"
                                                errorMessage={errors.title}
                                                isTouched={touched.title}
                                                required
                                            />
                                        )}
                                        <Field
                                            component={CustomInputField}
                                            label={
                                                comment
                                                    ? t("screen.applicantNotes.comment")
                                                    : t("screen.applicantNotes.note")
                                            }
                                            name="content"
                                            type="text"
                                            multiline
                                            rows={isMobile ? 12 : 14}
                                            errorMessage={errors.content}
                                            isTouched={touched.content}
                                            required
                                        />
                                        <Field
                                            component={CustomAutoComplete}
                                            options={users ?? []}
                                            placeholder={t("screen.applicantNotes.notify")}
                                            name="mentions"
                                            disabled={edit}
                                            value={values.mentions}
                                            setFieldValue={setFieldValue}
                                            style={{ width: "100%" }}
                                        />
                                        <div style={{ minHeight: 36 }}>
                                            <ErrorMessagesList errors={errors} touched={touched} />
                                        </div>
                                        <FormButtons
                                            labelBack={t("screen.dataUpload.dialog.cancel")}
                                            labelSubmit={t("screen.applicantDetails.save")}
                                            onBack={handleClose}
                                            style={{
                                                justifyContent: "flex-end",
                                                marginBottom: 0
                                            }}
                                        />
                                    </div>
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </HrBotDialog>
    );
};
